import { COUNTRIES, LOCALE_NAMES } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import {
  ACTIVITY_DATE_FIELDS,
  type ClientViewFilter,
  type EditFieldsResponse,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, useTransition } from "react";
import { type ActivityDateFieldValue } from "@/components/clients/activity-date-fields";
import {
  buildFilterRegistry,
  type DraftFilterValue,
  FilterValueEditor,
  pickerOptions,
  resolveFilterField,
  valuePickerLabels,
} from "@/components/clients/filters";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ValuePickerDialog } from "@/components/ui/value-picker-dialog";
import { patch, post } from "@/lib/api/_client";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useEditFields } from "@/lib/hooks/use-clients";
import { useTeams } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { ACTIVITY_DATE_LABELS, T } from "./client-view-dialog.i18n";
import type { ClientView } from "./types";

type ClientViewDialogProps = {
  open: boolean;
  view: ClientView | null;
  tenantUsers: Array<{ id: string; name: string }>;
  supportedLanguages: string[];
  onClose: () => void;
};

/** One legacy rolling-window activity-date filter, edited with its own dedicated (unchanged) UI. */
type LegacyActivityDateRow = {
  source: "legacy-activityDate";
  dateField: string;
  operator: "in_last" | "not_in_last";
  months: number;
};

/** One filter row driven by the shared registry — everything except the legacy shape above. */
type RegistryRow = {
  source: "registry";
  key: string;
  draft: DraftFilterValue;
};

/**
 * A stored filter this dialog's registry cannot resolve — e.g. one on a
 * deactivated custom field. Shown read-only and written back untouched on
 * Save, so editing a view never silently deletes filters it cannot display.
 */
type OpaqueRow = {
  source: "opaque";
  filter: ClientViewFilter;
};

type FilterRow = LegacyActivityDateRow | RegistryRow | OpaqueRow;

export function ClientViewDialog({
  open,
  view,
  tenantUsers,
  supportedLanguages,
  onClose,
}: ClientViewDialogProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [description, setDescription] = useState("");
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const [isDefault, setIsDefault] = useState(false);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  // State for the values selection sub-dialog (isAnyOf / containsAnyOf only)
  const [valuesDialogOpen, setValuesDialogOpen] = useState(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(null);

  const { editFields, isLoading: editFieldsLoading } = useEditFields();
  const { clientCostCategories, isLoading: costCategoriesLoading } = useClientCostCategories();
  const { teams, isLoading: teamsLoading } = useTeams();
  // Filters are resolved against the registry, so they are seeded (and Save
  // enabled) only once its async inputs have arrived — seeding earlier would
  // misread every custom-field filter as unresolvable.
  const registryReady = !editFieldsLoading && !costCategoriesLoading && !teamsLoading;

  const registry = useMemo(
    () =>
      buildFilterRegistry({
        userLanguage,
        localize,
        customFields: editFields as EditFieldsResponse,
        tenantUsers,
        supportedLanguages,
        costCategories: clientCostCategories.filter((c) => c.isActive),
        teams,
        countries: COUNTRIES,
        localeNames: LOCALE_NAMES,
      }),
    [
      userLanguage,
      localize,
      editFields,
      tenantUsers,
      supportedLanguages,
      clientCostCategories,
      teams,
    ],
  );
  const standardFields = registry.filter((f) => f.group === "standard");
  const customFields = registry.filter((f) => f.group === "custom");
  const fieldByKey = new Map(registry.map((f) => [f.key, f]));

  useEffect(() => {
    if (view) {
      // Extract user's language value from nameI18n
      const viewNameI18n = view.nameI18n || {};
      const nameInUserLang = viewNameI18n[userLanguage] || Object.values(viewNameI18n)[0] || "";
      const otherNameTranslations = Object.fromEntries(
        Object.entries(viewNameI18n).filter(([lang]) => lang !== userLanguage),
      );
      setName(nameInUserLang);
      setNameI18n(otherNameTranslations);

      // Extract user's language value from descriptionI18n
      const viewDescI18n = view.descriptionI18n || {};
      const descInUserLang = viewDescI18n[userLanguage] || Object.values(viewDescI18n)[0] || "";
      const otherDescTranslations = Object.fromEntries(
        Object.entries(viewDescI18n).filter(([lang]) => lang !== userLanguage),
      );
      setDescription(descInUserLang);
      setDescriptionI18n(otherDescTranslations);

      setIsDefault(view.isDefault);
    } else {
      setName("");
      setNameI18n({});
      setDescription("");
      setDescriptionI18n({});
      setIsDefault(false);
    }
    setError(null);
  }, [view, open, userLanguage]);

  useEffect(() => {
    if (!registryReady) return;
    setFilters(
      (view?.filters ?? []).map((f): FilterRow => {
        if (f.type === "activityDate") {
          return {
            source: "legacy-activityDate",
            dateField: f.dateField,
            operator: f.operator,
            months: f.months,
          };
        }
        const resolved = resolveFilterField(f, registry);
        return resolved
          ? { source: "registry", key: resolved.field.key, draft: resolved.draft }
          : { source: "opaque", filter: f };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `registry` is rebuilt every render; re-seed only on view/open/language change or once its inputs have loaded
  }, [view, open, userLanguage, registryReady]);

  const handleAddFilter = () => {
    const firstKey = standardFields[0]?.key ?? customFields[0]?.key;
    if (!firstKey) return;
    setFilters([...filters, { source: "registry", key: firstKey, draft: {} }]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, key: string) => {
    const newFilters = [...filters];
    newFilters[index] = { source: "registry", key, draft: {} };
    setFilters(newFilters);
  };

  const handleDraftChange = (index: number, patch: DraftFilterValue) => {
    const row = filters[index];
    if (row.source !== "registry") return;
    const newFilters = [...filters];
    newFilters[index] = { ...row, draft: { ...row.draft, ...patch } };
    setFilters(newFilters);
  };

  const handleOpenValuesDialog = (index: number) => {
    setEditingFilterIndex(index);
    setValuesDialogOpen(true);
  };

  const handleSaveValues = (values: string[]) => {
    if (editingFilterIndex !== null) handleDraftChange(editingFilterIndex, { values });
    setValuesDialogOpen(false);
    setEditingFilterIndex(null);
  };

  const handleLegacyActivityDateChange = (
    index: number,
    patch: Partial<Omit<LegacyActivityDateRow, "source">>,
  ) => {
    const row = filters[index];
    if (row.source !== "legacy-activityDate") return;
    const newFilters = [...filters];
    newFilters[index] = { ...row, ...patch };
    setFilters(newFilters);
  };

  const tDates = useT(ACTIVITY_DATE_LABELS);
  const getActivityDateLabel = (fieldValue: string): string =>
    tDates[fieldValue as ActivityDateFieldValue] ?? fieldValue;

  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    const apiFilters: ClientViewFilter[] = filters
      .map((row): ClientViewFilter | null => {
        if (row.source === "legacy-activityDate") {
          if (!row.dateField || !row.operator || !row.months) return null;
          return {
            type: "activityDate",
            dateField: row.dateField as
              | "lastLogin"
              | "lastInfoUpdated"
              | "lastFlowActivity"
              | "lastFlowCompleted"
              | "lastAccountSynced"
              | "lastSignedDocument",
            operator: row.operator,
            months: row.months,
          };
        }
        if (row.source === "opaque") return row.filter;
        const field = fieldByKey.get(row.key);
        return field ? field.toFilter(row.draft) : null;
      })
      .filter((f): f is ClientViewFilter => f !== null);

    startTransition(async () => {
      try {
        const url = view ? `/internal/client-views/${view.id}` : "/internal/client-views";
        const payload = {
          name: name.trim(),
          nameI18n,
          description: description.trim() || undefined,
          descriptionI18n,
          filters: apiFilters,
          isDefault,
          userLanguage,
        };
        if (view) {
          await patch(url, payload);
        } else {
          await post(url, payload);
        }

        queryClient.invalidateQueries({ queryKey: ["clientViews"] });
        onClose();
      } catch (err: any) {
        setError(err.message || t.failedToSave);
      }
    });
  };

  const editingRow = editingFilterIndex !== null ? filters[editingFilterIndex] : null;
  const editingField =
    editingRow?.source === "registry" ? (fieldByKey.get(editingRow.key) ?? null) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{view ? t.editView : t.addNewView}</DialogTitle>
            <DialogDescription>
              {view ? t.updateViewDescription : t.createViewDescription}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <LocalizedInput
                  id="viewName"
                  label={t.name}
                  value={name}
                  i18nValue={nameI18n}
                  onChange={setName}
                  onI18nChange={setNameI18n}
                  supportedLanguages={supportedLanguages}
                  placeholder={t.viewNamePlaceholder}
                  required
                  disabled={isPending}
                />
              </div>

              <div className="space-y-2">
                <LocalizedInput
                  id="viewDescription"
                  label={t.description}
                  value={description}
                  i18nValue={descriptionI18n}
                  onChange={setDescription}
                  onI18nChange={setDescriptionI18n}
                  supportedLanguages={supportedLanguages}
                  placeholder={t.optionalDescription}
                  disabled={isPending}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isDefault"
                  checked={isDefault}
                  onCheckedChange={(checked) => setIsDefault(checked === true)}
                  disabled={isPending}
                />
                <Label htmlFor="isDefault" className="font-normal cursor-pointer">
                  {t.setAsDefault}
                </Label>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>{t.filters}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddFilter}
                    disabled={!registryReady}
                  >
                    {t.addFilter}
                  </Button>
                </div>

                {filters.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t.noFiltersHint}</p>
                )}

                {filters.map((row, index) => {
                  const field = row.source === "registry" ? fieldByKey.get(row.key) : undefined;
                  return (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: multiple filters of the same field are valid
                      key={`filter-${index}`}
                      className="border rounded-lg p-3 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">{t.filterNumber(String(index + 1))}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          tooltip={t.delete}
                          onClick={() => handleRemoveFilter(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {row.source === "opaque" ? (
                        <p className="text-sm text-muted-foreground">{t.unavailableFilter}</p>
                      ) : row.source === "legacy-activityDate" ? (
                        <div className="space-y-3">
                          <Label className="text-xs text-muted-foreground">{t.activityDate}</Label>
                          <Select
                            value={row.dateField || "lastLogin"}
                            onValueChange={(value) =>
                              handleLegacyActivityDateChange(index, { dateField: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t.selectDateField} />
                            </SelectTrigger>
                            <SelectContent>
                              {ACTIVITY_DATE_FIELDS.map((field) => (
                                <SelectItem key={field} value={field}>
                                  {getActivityDateLabel(field)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <div className="flex gap-2">
                            <Select
                              value={row.operator || "not_in_last"}
                              onValueChange={(value) =>
                                handleLegacyActivityDateChange(index, {
                                  operator: value as "in_last" | "not_in_last",
                                })
                              }
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="in_last">{t.inLast}</SelectItem>
                                <SelectItem value="not_in_last">{t.notInLast}</SelectItem>
                              </SelectContent>
                            </Select>

                            <Select
                              value={String(row.months || 6)}
                              onValueChange={(value) =>
                                handleLegacyActivityDateChange(index, {
                                  months: parseInt(value, 10),
                                })
                              }
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">{t.monthSingular}</SelectItem>
                                <SelectItem value="3">{t.monthsPlural("3")}</SelectItem>
                                <SelectItem value="6">{t.monthsPlural("6")}</SelectItem>
                                <SelectItem value="12">{t.monthsPlural("12")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Select
                            value={row.key}
                            onValueChange={(value) => handleFieldChange(index, value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>{t.standardFields}</SelectLabel>
                                {standardFields.map((f) => (
                                  <SelectItem key={f.key} value={f.key}>
                                    {f.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                              {customFields.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel>{t.clientFields}</SelectLabel>
                                  {customFields.map((f) => (
                                    <SelectItem key={f.key} value={f.key}>
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                            </SelectContent>
                          </Select>

                          {field && (
                            <FilterValueEditor
                              field={field}
                              draft={row.draft}
                              onChange={(patch) => handleDraftChange(index, patch)}
                              onOpenValuesDialog={() => handleOpenValuesDialog(index)}
                              userLanguage={userLanguage}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending} disabled={!registryReady}>
                {view ? t.save : t.addView}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {editingField && (
        <ValuePickerDialog
          open={valuesDialogOpen}
          options={pickerOptions(
            editingField.options,
            editingRow?.source === "registry" ? (editingRow.draft.values ?? []) : [],
            editingField.unresolvedLabel,
          )}
          selectedValues={editingRow?.source === "registry" ? (editingRow.draft.values ?? []) : []}
          labels={valuePickerLabels(userLanguage, editingField.label)}
          onClose={() => {
            setValuesDialogOpen(false);
            setEditingFilterIndex(null);
          }}
          onSave={handleSaveValues}
        />
      )}
    </>
  );
}
